import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Home from "./pages/Home";
import { Toaster } from "./components/ui/sonner";
import { trackEvent } from "./lib/analytics";
import { RouteSeo } from "./components/Seo";

const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const Support = lazy(() => import("./pages/Support"));
const Membership = lazy(() => import("./pages/Membership"));
const Shop = lazy(() => import("./pages/Shop"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCancelled = lazy(() => import("./pages/PaymentCancelled"));
const RefundShipping = lazy(() => import("./pages/RefundShipping"));
const FindMachine = lazy(() => import("./pages/FindMachine"));
const Admin = lazy(() => import("./pages/Admin"));
const Credits = lazy(() => import("./pages/Credits"));
const CreditsSuccess = lazy(() => import("./pages/CreditsSuccess"));
const Preorder = lazy(() => import("./pages/Preorder"));
const PreorderSuccess = lazy(() => import("./pages/PreorderSuccess"));

const RouteFallback = () => (
    <div className="min-h-screen flex items-center justify-center bg-[#faf7f2]" data-testid="route-loading">
        <div className="w-10 h-10 border-4 border-amber-800/20 border-t-amber-800 rounded-full animate-spin" />
    </div>
);

const ScrollToTop = () => {
    const location = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);
    return null;
};

const PageViewTracker = () => {
    const location = useLocation();
    useEffect(() => {
        if (!location.pathname.startsWith("/admin")) {
            trackEvent("page_view", location.pathname);
        }
    }, [location.pathname]);
    return null;
};

const AppRoutes = () => (
    <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/support" element={<Support />} />
        <Route path="/membership" element={<Membership />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/find-a-machine" element={<FindMachine />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/credits" element={<Credits />} />
        <Route path="/credits-success" element={<CreditsSuccess />} />
        <Route path="/preorder" element={<Preorder />} />
        <Route path="/preorder-success" element={<PreorderSuccess />} />
        <Route path="/blog" element={<Navigate to="/" replace />} />
        <Route path="/blog/:slug" element={<Navigate to="/" replace />} />
        <Route path="/subscribe" element={<Navigate to="/membership" replace />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/payment-cancelled" element={<PaymentCancelled />} />
        <Route path="/refund-and-shipping" element={<RefundShipping />} />
        <Route path="*" element={<Navigate to="/" replace />} />
        <Route path="/pickup-and-refunds" element={<RefundShipping />} />
    </Routes>
);

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <ScrollToTop />
                <PageViewTracker />
                <RouteSeo />
                <Suspense fallback={<RouteFallback />}>
                    <AppRoutes />
                </Suspense>
            </BrowserRouter>
            <Toaster position="bottom-right" richColors />
        </div>
    );
}

export default App;
